import {
  Injectable,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
} from '@angular/core';
import { StartAnalysisDialog } from '@shared/components/start-analysis-dialog/start-analysis-dialog';
import {
  Analysis,
  FileOverviewItem,
  SelectMediaDto,
} from '@shared/models/analysis.model';

interface ExecutionDialogOptions {
  analysis: Analysis;
  organizationId: string;
  taskWindow: { startTask: number; endTask: number };
  filesOverview: FileOverviewItem[];
  currentSelectedMedia: SelectMediaDto | null;
  currentSelectedTemplateId: string | null;
  prospective: boolean;
  requiresMedia: boolean;
  requiresTemplate: boolean;
}

/**
 * Service for showing start analysis confirmation dialogs
 * Provides a Promise-based API for getting user confirmation with media selection
 */
@Injectable({
  providedIn: 'root',
})
export class StartAnalysisDialogService {
  private componentRef?: ReturnType<
    typeof createComponent<StartAnalysisDialog>
  >;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
  ) {}

  /**
   * Ask for the input file and persist the choice on the analysis.
   * Executing is the caller's job — see ExecutionPreflightService.
   * @returns true if a file was selected and persisted, false if cancelled
   */
  async confirmExecution(options: ExecutionDialogOptions): Promise<boolean> {
    // Create component if it doesn't exist
    if (!this.componentRef) {
      this.componentRef = createComponent(StartAnalysisDialog, {
        environmentInjector: this.injector,
      });

      // Attach to application
      this.appRef.attachView(this.componentRef.hostView);

      // Append to body
      const domElem = this.componentRef.location.nativeElement;
      document.body.appendChild(domElem);
    }

    // Set inputs via setInput() so signal inputs are updated reactively
    this.componentRef.setInput('analysis', options.analysis);
    this.componentRef.setInput('organizationId', options.organizationId);
    this.componentRef.setInput('taskWindow', options.taskWindow);
    this.componentRef.setInput('filesOverview', options.filesOverview);
    this.componentRef.setInput('currentSelectedMedia', options.currentSelectedMedia);
    this.componentRef.setInput('currentSelectedTemplateId', options.currentSelectedTemplateId);
    this.componentRef.setInput('prospective', options.prospective);
    this.componentRef.setInput('requiresMedia', options.requiresMedia);
    this.componentRef.setInput('requiresTemplate', options.requiresTemplate);

    // Open the dialog and wait for user response
    const instance = this.componentRef.instance;
    instance.open();

    return new Promise<boolean>((resolve) => {
      const confirmedSub = instance.confirmed.subscribe(() => {
        confirmedSub.unsubscribe();
        cancelledSub.unsubscribe();
        resolve(true);
      });

      const cancelledSub = instance.cancelled.subscribe(() => {
        confirmedSub.unsubscribe();
        cancelledSub.unsubscribe();
        resolve(false);
      });
    });
  }
}
